import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Camera, Save, Loader, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../contexts/AuthContext';
import { auth, getFreshToken, storage } from '../lib/firebase';
import { updateProfile } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const AccountSettingsPage = () => {
  const { user, isAuthenticated } = useAuth();

  // Profile state
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [bio, setBio] = useState('');
  const [photoPreview, setPhotoPreview] = useState(user?.photoURL || auth.currentUser?.photoURL || null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const fileInputRef = useRef(null);

  // Notification prefs
  const [notifications, setNotifications] = useState({});


  // Sync photo from Firebase auth when user object updates
  useEffect(() => {
    const url = auth.currentUser?.photoURL || user?.photoURL;
    if (url && !photoPreview) setPhotoPreview(url);
  }, [user]);

  // Load bio + notification prefs from backend on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const token = await getFreshToken();
        const res = await fetch(`${BACKEND}/api/account/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.bio) setBio(data.bio);
          if (data.notifications) setNotifications(data.notifications);
        }
      } catch (e) {
        console.error('Failed to load profile:', e);
      }
    })();
  }, [isAuthenticated]);

  const toggleNotif = async (key) => {
    const next = { ...notifications, [key]: !notifications[key] };
    setNotifications(next);
    try {
      const token = await getFreshToken();
      await fetch(`${BACKEND}/api/account/profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notifications: next }),
      });
    } catch (e) {
      console.error('Failed to save notification prefs:', e);
    }
  };

  // Save profile — display name to Firebase, bio to MongoDB
  const handleSaveProfile = async () => {
    if (!auth.currentUser) return;
    setSaving(true);
    setSaveMessage('');
    try {
      // Update Firebase display name
      await updateProfile(auth.currentUser, {
        displayName: displayName.trim() || auth.currentUser.displayName,
      });

      // Save bio to backend
      const token = await getFreshToken();
      await fetch(`${BACKEND}/api/account/profile`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bio: bio.trim() }),
      });

      setSaveMessage('Profile updated!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (e) {
      console.error('Profile update failed:', e);
      setSaveMessage('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  // Upload photo to Firebase Storage and update profile
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser) return;

    setUploadingPhoto(true);
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `avatars/${auth.currentUser.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, { photoURL: downloadURL });
      setPhotoPreview(downloadURL);
      setSaveMessage('Photo updated!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Photo upload failed:', err);
      setSaveMessage('Failed to upload photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-24 text-center">
          <p className="text-gray-600 mb-4">Please sign in to access account settings.</p>
          <Link to="/membership"><Button>Sign In</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title="Account Settings" noindex />
      <Header />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-600 mt-1">Manage your profile and preferences.</p>
        </div>

        <div className="space-y-6">
          {/* ── Profile ─────────────────────────────────── */}
          <Card>
            <CardHeader><CardTitle>Profile</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {/* Photo */}
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center text-3xl text-gray-400">
                    {uploadingPhoto
                      ? <Loader className="w-6 h-6 animate-spin text-gray-400" />
                      : photoPreview
                        ? <img src={photoPreview} alt="Avatar" className="w-full h-full object-cover" />
                        : (user?.displayName?.[0]?.toUpperCase() || '?')}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Camera className="w-3.5 h-3.5" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{user?.displayName || user?.email}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Your name"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tell us about yourself..."
                />
              </div>

              <div className="flex items-center gap-3">
                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Profile
                </Button>
                {saveMessage && (
                  <span className={`text-sm ${saveMessage.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                    {saveMessage}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Email & Password ─────────────────────────── */}
          <Card>
            <CardHeader><CardTitle>Email & Password</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full rounded-md border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Email changes are managed through your sign-in provider.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
                    fetch(`${backendUrl}/api/auth/send-password-reset`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: user.email }),
                    })
                      .then((res) => {
                        if (res.ok) alert('Password reset email sent!');
                        else alert('Failed to send reset email. Try again.');
                      })
                      .catch(() => alert('Failed to send reset email. Try again.'));
                  }}
                >
                  Send Password Reset Email
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* ── Notifications ────────────────────────────── */}
          <Card>
            <CardHeader><CardTitle>Notifications</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { key: 'new_walks', label: 'New walks in my area', desc: 'Get notified when new walks are published near you.' },
                  { key: 'weekly_digest', label: 'Weekly digest', desc: 'A summary of new walks, tips, and community highlights.' },
                  { key: 'community_updates', label: 'Community updates', desc: 'Group walk invitations and community news.' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{label}</p>
                      <p className="text-xs text-gray-500">{desc}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleNotif(key)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        notifications[key] ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                          notifications[key] ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ── Danger Zone ───────────────────────────────── */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete your account? This cannot be undone.')) {
                    if (window.confirm('This will permanently delete all your data. Type your email to confirm.')) {
                      alert('Account deletion is not yet implemented. Please contact support.');
                    }
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccountSettingsPage;
